import { makeObservable, observable, action } from 'mobx'
import { initHelpDeskSystem } from 'tracking/helpDeskSystem'

import {
  getDefaultResponse,
  intentRegistry,
} from '../components/KawaconnChatbot/config/DefaultIntents'
import type {
  IntentResult,
  ResponseTemplate,
} from '../components/KawaconnChatbot/types/intent'

import appStore from './appStore'

export interface Message {
  id: string
  text: string | React.ReactElement
  sender: 'user' | 'bot'
  timestamp: Date
  type?: 'text' | 'image' | 'file'
  intentData?: IntentResult // Add intent info
  template?: ResponseTemplate // Add response template
}

class ChatbotStore {
  activeChatbot: string | null = null
  messages: Message[] = []
  isLoading = false

  constructor() {
    makeObservable(this, {
      activeChatbot: observable,
      messages: observable,
      isLoading: observable,
      setActiveChatbot: action,
      closeAllChatbots: action,
      addMessage: action,
      setMessages: action,
      clearMessages: action,
      setLoading: action,
      initializeWelcomeMessage: action,
      resetChatSession: action,
    })

    intentRegistry.initializeDefaultIntents()
    // Initialize with welcome message if messages are empty
    this.initializeWelcomeMessage()
  }

  // Initialize with welcome message if messages are empty
  initializeWelcomeMessage = () => {
    if (this.messages.length === 0) {
      // Get default response content to guide users
      const defaultResponse = getDefaultResponse({})

      const welcomeElement = (
        <div>
          <div className="font-bold mb-2">
            🤖 Welcome to Kawaconn AI Assistant!
          </div>
          <div className="mb-2">
            I can help you check specialist availability.
          </div>
          <div>{defaultResponse.response}</div>
        </div>
      )

      this.messages.push({
        id: '1',
        text: welcomeElement,
        sender: 'bot',
        timestamp: new Date(),
        type: 'text',
      })
    }
  }

  addMessage = (message: Message) => {
    this.messages.push(message)
  }

  setMessages = (messages: Message[]) => {
    this.messages = messages
  }

  clearMessages = () => {
    this.messages = []
    this.initializeWelcomeMessage()
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
  }

  // Reset chat session - useful when switching between different chat contexts
  resetChatSession = () => {
    this.clearMessages()
    this.setLoading(false)
  }

  setActiveChatbot = (chatbotType: string | null) => {
    // Đóng tất cả chatbot trước khi mở chatbot mới
    if (chatbotType !== this.activeChatbot) {
      this.closeAllChatbots()
    }

    this.activeChatbot = chatbotType

    // Gọi các hàm để mở chatbot cụ thể
    if (chatbotType === 'knowledge-base') {
      this.openChatbase()
    } else if (chatbotType === 'support') {
      this.openFreshworks()
    }
  }

  closeAllChatbots = () => {
    // Đóng Chatbase
    this.closeChatbase()

    // Đóng Freshworks Widget
    if ((window as any).FreshworksWidget) {
      ;(window as any).FreshworksWidget('hide')
    }

    this.activeChatbot = null
  }

  toggleChatbaseButton(visible = false) {
    const toggleButton = window.document.getElementById(
      'chatbase-bubble-button'
    )
    if (toggleButton) {
      toggleButton.style.setProperty(
        'display',
        visible ? 'block' : 'none',
        'important'
      )
    }
  }

  private closeChatbase() {
    if ((window as any).chatbase?.close) {
      ;(window as any).chatbase.close()
      this.toggleChatbaseButton(false)
    }
  }

  private openChatbase = () => {
    if ((window as any).chatbase?.open) {
      ;(window as any).chatbase.open()
      const toggleButton = window.document.getElementById(
        'chatbase-bubble-button'
      )
      if (toggleButton) {
        toggleButton.style.setProperty('display', 'block', 'important')
      }
    }
  }

  private openFreshworks = () => {
    this.openHelpDesk(appStore?.currentTenant, appStore?.currentUser)
  }

  helpDeskInitialized = false

  private openHelpDesk = (currentTenant: any, currentUser: any) => {
    const global: any = window

    if (!this.helpDeskInitialized) {
      initHelpDeskSystem(currentTenant, currentUser)
      this.helpDeskInitialized = true

      setTimeout(() => {
        global.FreshworksWidget?.('open')
      }, 1500)
    } else {
      global.FreshworksWidget?.('open')
    }
  }
}

export const chatbotStore = new ChatbotStore()
export default chatbotStore
